/**
 * TeamSystem Impact - Compliance Path · Data Hook
 *
 * Aggrega i dati reali delle quattro tappe del "respiro" della compliance
 * (Engagement, Doppia Materialità, Raccolta) da più API, calcolando stato e
 * percentuale tramite phaseModel. Reporting non ha dati: è "in arrivo".
 */

import { useState, useEffect } from 'react';
import { engagementApi, type Campaign } from '../../engagement/api';
import { iroApi, type IRO } from '../../dma/api';
import { macroCategoryApi, type MacroCategory } from '../../dma/api/macro-category.api';
import { getEffectiveScores } from '../../dma/utils/score-resolution';
import { dashboardApi } from '../../data-capture/api/dashboard.api';
import type { CampaignProgressEntry, DataCaptureProgress } from '@teamsystem-impact/shared';
import { logger } from '@/config/logger';
import {
  engagementPhase,
  dmaPhase,
  collectionPhase,
  isDmaConsolidated,
  isCarriedOverAssessment,
  dmaAssessedWithFallback,
  type PhaseResult,
} from './phaseModel';

export interface MaterialArea {
  id: string;
  name: string;
  nameEN: string;
  count: number;
}

export interface CompliancePathData {
  loading: boolean;
  engagement: {
    result: PhaseResult;
    campaignsTotal: number;
    responses: number;
    campaigns: Campaign[];
    responsesByCampaign: Record<string, number>;
  };
  dma: {
    result: PhaseResult;
    totalIros: number;
    materialIros: number;
    consolidated: boolean;
    areas: MaterialArea[];
  };
  collection: {
    result: PhaseResult;
    datapointCount: number;
    overdue: number;
    frameworks: string[];
    campaigns: CampaignProgressEntry[];
    compiled: number;
    validated: number;
    totalDp: number;
    compiledPct: number;
    validatedPct: number;
  };
}

const EMPTY: CompliancePathData = {
  loading: true,
  engagement: { result: { state: 'not_started', percent: 0 }, campaignsTotal: 0, responses: 0, campaigns: [], responsesByCampaign: {} },
  dma: { result: { state: 'not_started', percent: 0 }, totalIros: 0, materialIros: 0, consolidated: false, areas: [] },
  collection: { result: { state: 'not_started', percent: 0 }, datapointCount: 0, overdue: 0, frameworks: [], campaigns: [], compiled: 0, validated: 0, totalDp: 0, compiledPct: 0, validatedPct: 0 },
};

function macroCatId(iro: IRO): string | undefined {
  const m = iro.macroCategoryId;
  if (!m) return undefined;
  return typeof m === 'string' ? m : m._id;
}

/**
 * Macro-aree "materiali" secondo lo STESSO criterio della matrice di materialità
 * (MaterialityDashboard): una macro-categoria è materiale (`isMaterial`) se è SOPRA
 * ENTRAMBE le soglie, cioè `maxImpact >= sogliaImpatto && maxFinancial >= sogliaFinanziaria`.
 * Le soglie arrivano da `iroApi.getMatrixData(year).thresholds`.
 * Usa l'elenco COMPLETO degli IRO dell'anno, non solo quelli material.
 */
function materialMacroAreas(
  allIros: IRO[],
  macroCats: MacroCategory[],
  impactTh: number,
  financialTh: number,
): MaterialArea[] {
  const byId = new Map<string, MacroCategory>();
  macroCats.forEach((c) => byId.set(c._id, c));

  return macroCats
    .map((cat) => {
      const catIROs = allIros.filter((iro) => macroCatId(iro) === cat._id);
      if (catIROs.length === 0) return null;

      // Asse X: max impactScore effettivo tra gli IRO di tipo 'impact'
      const impactIROs = catIROs.filter((iro) => iro.type === 'impact');
      const maxImpact = impactIROs.length > 0
        ? Math.max(...impactIROs.map((iro) => getEffectiveScores(iro).impactScore))
        : 0;

      // Asse Y: max financialScore effettivo tra gli IRO di tipo 'risk'/'opportunity'
      const finIROs = catIROs.filter((iro) => iro.type === 'risk' || iro.type === 'opportunity');
      const maxFinancial = finIROs.length > 0
        ? Math.max(...finIROs.map((iro) => getEffectiveScores(iro).financialScore))
        : 0;

      // Materiale = sopra ENTRAMBE le soglie (stesso criterio `isMaterial` della matrice)
      if (!(maxImpact >= impactTh && maxFinancial >= financialTh)) return null;

      const cd = byId.get(cat._id);
      return {
        id: cat._id,
        name: cd?.name ?? cat._id,
        nameEN: cd?.nameEN ?? cd?.name ?? cat._id,
        count: catIROs.length,
        _sort: maxImpact + maxFinancial,
      };
    })
    .filter((x): x is MaterialArea & { _sort: number } => x !== null)
    .sort((a, b) => b._sort - a._sort)
    .map(({ _sort, ...area }) => area);
}

export function useCompliancePath(year: number): CompliancePathData {
  const [data, setData] = useState<CompliancePathData>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    setData((prev) => ({ ...prev, loading: true }));

    const load = async () => {
      const [
        campaignsR,
        statsR,
        iroStatsR,
        allIrosR,
        macroCatsR,
        matrixR,
        phaseStatusR,
        progressR,
        campaignProgressR,
      ] = await Promise.allSettled([
        engagementApi.fetchCampaigns(year),
        engagementApi.fetchStats(year),
        iroApi.getStats(year),
        iroApi.list({ assessmentYear: year, pageSize: 1000 }).then((r) => r.items),
        macroCategoryApi.list(year),
        iroApi.getMatrixData(year),
        dashboardApi.getPhaseStatus(year),
        dashboardApi.getProgress(year),
        dashboardApi.getCampaignProgress(year),
      ]);

      if (cancelled) return;

      // --- Engagement ---
      const campaigns = campaignsR.status === 'fulfilled' ? campaignsR.value : [];
      const stats = statsR.status === 'fulfilled' ? statsR.value : null;
      const campaignsTotal = campaigns.length;
      const campaignsClosed = campaigns.filter((c) => c.status === 'closed').length;
      const responses = stats?.totalEntries ?? 0;
      const responsesByCampaign: Record<string, number> = {};
      stats?.byCampaign?.forEach((b) => { responsesByCampaign[b.campaignId] = b.count; });

      // --- DMA ---
      const iroStats = iroStatsR.status === 'fulfilled' ? iroStatsR.value : null;
      const allIros = allIrosR.status === 'fulfilled' ? allIrosR.value : [];
      const macroCats = macroCatsR.status === 'fulfilled' ? macroCatsR.value : [];
      const matrix = matrixR.status === 'fulfilled' ? matrixR.value : null;
      const impactTh = matrix?.thresholds?.impactScore ?? 0;
      const financialTh = matrix?.thresholds?.financialScore ?? 0;
      const totalIros = iroStats?.total ?? 0;
      // Le decisioni riportate dall'anno precedente (clonazione con "copia
      // valutazioni") non sono esiti dell'anno: restano fuori da avanzamento,
      // IRO materiali e macro-aree finché qualcuno non rivaluta.
      const { assessed, material: materialIros } = dmaAssessedWithFallback(
        allIrosR.status === 'fulfilled' ? allIros : null,
        iroStats?.byStatus,
      );
      const dmaInput = { total: totalIros, assessed };
      const yearIros = allIros.filter((iro) => !isCarriedOverAssessment(iro));

      // --- Collection ---
      const phaseStatus = phaseStatusR.status === 'fulfilled' ? phaseStatusR.value : null;
      const progress: DataCaptureProgress | null = progressR.status === 'fulfilled' ? progressR.value : null;
      const campaignProgress = campaignProgressR.status === 'fulfilled' ? campaignProgressR.value.campaigns : [];
      const frameworks = (progress?.byESRS ?? []).map((g) => g.standard);

      const sumC = (sel: (e: CampaignProgressEntry) => number) => campaignProgress.reduce((a, e) => a + sel(e), 0);
      const totalDp = sumC((e) => e.kpi.total);
      const compiled = sumC((e) => e.kpi.compiled);
      const validated = sumC((e) => e.kpi.completed);
      const compiledPct = totalDp > 0 ? Math.round((compiled / totalDp) * 100) : 0;
      const validatedPct = totalDp > 0 ? Math.round((validated / totalDp) * 100) : 0;

      if (campaignsR.status === 'rejected') logger.error('CompliancePath: engagement campaigns failed', campaignsR.reason);
      if (iroStatsR.status === 'rejected') logger.error('CompliancePath: dma stats failed', iroStatsR.reason);
      if (allIrosR.status === 'rejected') logger.error('CompliancePath: dma iro list failed', allIrosR.reason);
      if (matrixR.status === 'rejected') logger.error('CompliancePath: dma matrix failed', matrixR.reason);
      if (phaseStatusR.status === 'rejected') logger.error('CompliancePath: phase status failed', phaseStatusR.reason);

      setData({
        loading: false,
        engagement: {
          result: engagementPhase({ campaignsTotal, campaignsClosed, responses }),
          campaignsTotal,
          responses,
          campaigns,
          responsesByCampaign,
        },
        dma: {
          result: dmaPhase(dmaInput),
          totalIros,
          materialIros,
          consolidated: isDmaConsolidated(dmaInput),
          areas: materialMacroAreas(yearIros, macroCats, impactTh, financialTh),
        },
        collection: {
          result: collectionPhase({
            configurationComplete: phaseStatus?.configurationComplete ?? false,
            collectionComplete: phaseStatus?.collectionComplete ?? false,
            datapointCount: phaseStatus?.datapointCount ?? progress?.total ?? 0,
            completionRate: validatedPct || (progress?.completionRate ?? 0),
          }),
          datapointCount: phaseStatus?.datapointCount ?? progress?.total ?? 0,
          overdue: progress?.overdue ?? 0,
          frameworks,
          campaigns: campaignProgress,
          compiled,
          validated,
          totalDp,
          compiledPct,
          validatedPct,
        },
      });
    };

    load().catch((err) => {
      logger.error('CompliancePath: unexpected load error', err);
      if (!cancelled) setData((prev) => ({ ...prev, loading: false }));
    });

    return () => { cancelled = true; };
  }, [year]);

  return data;
}
